import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService, UserProfile } from '../../../services/auth.service';

export interface SessionModalData {
  type: 'token-expired' | 'session-warning' | 'user-profile' | 'cross-app-logout' | 'cross-app-login';
  message?: string;
  timeRemaining?: number;
}

@Component({
  selector: 'app-session-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="modal-overlay" *ngIf="isVisible" (click)="onOverlayClick($event)">
      <div class="modal-container" (click)="$event.stopPropagation()">
        
        <!-- Token Expired Modal -->
        <div *ngIf="data?.type === 'token-expired'" class="modal-content token-expired">
          <div class="modal-header">
            <h3>🔒 Sesión Expirada</h3>
            <button class="close-btn" (click)="closeModal()">&times;</button>
          </div>
          <div class="modal-body">
            <p>Tu sesión ha expirado por seguridad. Por favor, inicia sesión nuevamente para continuar.</p>
            <div class="warning-message">
              <span class="warning-icon">⚠️</span>
              <span>{{ data?.message || 'Token de acceso expirado' }}</span>
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn btn-secondary" (click)="logout()">
              Cerrar Sesión
            </button>
            <button class="btn btn-primary" (click)="refreshToken()">
              <span *ngIf="!isRefreshing">🔄 Renovar Sesión</span>
              <span *ngIf="isRefreshing" class="loading">
                <span class="spinner"></span> Renovando...
              </span>
            </button>
          </div>
        </div>

        <!-- Cross-App Logout Modal -->
        <div *ngIf="data?.type === 'cross-app-logout'" class="modal-content token-expired">
          <div class="modal-header">
            <h3>🚪 Sesión Finalizada</h3>
          </div>
          <div class="modal-body">
            <p>{{ data?.message || 'Tu sesión ha sido cerrada desde otra aplicación.' }}</p>
          </div>
          <div class="modal-actions">
            <button class="btn btn-primary" (click)="logout()">
              Entendido
            </button>
          </div>
        </div>

        <!-- Cross-App Login Modal -->
        <div *ngIf="data?.type === 'cross-app-login'" class="modal-content token-expired">
          <div class="modal-header">
            <h3>👋 Sesión Detectada</h3>
            <button class="close-btn" (click)="closeModal()">&times;</button>
          </div>
          <div class="modal-body">
            <p>{{ data?.message || 'Se ha detectado un inicio de sesión desde otra aplicación.' }}</p>
          </div>
          <div class="modal-actions">
            <button class="btn btn-primary" (click)="syncLogin()">
              Sincronizar Sesión
            </button>
          </div>
        </div>

        <!-- Session Warning Modal -->
        <div *ngIf="data?.type === 'session-warning'" class="modal-content session-warning">
          <div class="modal-header">
            <h3>⏰ Sesión por Expirar</h3>
            <button class="close-btn" (click)="closeModal()">&times;</button>
          </div>
          <div class="modal-body">
            <p>Tu sesión expirará pronto. ¿Deseas extender tu sesión?</p>
            <div class="countdown" *ngIf="data?.timeRemaining">
              <span class="countdown-label">Tiempo restante:</span>
              <span class="countdown-time">{{ formatTime(data?.timeRemaining || 0) }}</span>
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn btn-secondary" (click)="logout()">
              Cerrar Sesión
            </button>
            <button class="btn btn-primary" (click)="extendSession()">
              Extender Sesión
            </button>
          </div>
        </div>

        <!-- User Profile Modal -->
        <div *ngIf="data?.type === 'user-profile'" class="modal-content user-profile">
          <div class="modal-header">
            <h3>👤 Perfil de Usuario</h3>
            <button class="close-btn" (click)="closeModal()">&times;</button>
          </div>
          <div class="modal-body" *ngIf="userProfile">
            <div class="user-info">
              <div class="user-avatar">
                <span class="avatar-text">{{ getInitials(userProfile.displayName) }}</span>
              </div>
              <div class="user-details">
                <h4>{{ userProfile.displayName }}</h4>
                <p class="user-email">{{ userProfile.mail }}</p>
                <p class="user-title" *ngIf="userProfile.jobTitle">{{ userProfile.jobTitle }}</p>
                <p class="user-department" *ngIf="userProfile.department">{{ userProfile.department }}</p>
              </div>
            </div>
            
            <div class="user-roles" *ngIf="userProfile.roles.length > 0">
              <h5>Roles:</h5>
              <div class="roles-list">
                <span 
                  *ngFor="let role of userProfile.roles" 
                  class="role-badge"
                  [class.admin]="role === 'admin'"
                  [class.editor]="role === 'editor' || role === 'content.editor'"
                  [class.viewer]="role === 'viewer'">
                  {{ role | titlecase }}
                </span>
              </div>
            </div>

            <div class="session-info">
              <h5>Información de Sesión:</h5>
              <div class="session-details">
                <div class="session-item">
                  <span class="label">ID de Usuario:</span>
                  <span class="value">{{ userProfile.id }}</span>
                </div>
                <div class="session-item">
                  <span class="label">Último acceso:</span>
                  <span class="value">{{ lastAccessTime }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn btn-secondary" (click)="refreshToken()">
              🔄 Renovar Token
            </button>
            <button class="btn btn-danger" (click)="logout()">
              🚪 Cerrar Sesión
            </button>
          </div>
        </div>

      </div>
    </div>
  `,
  styleUrls: ['./session-modal.component.scss']
})
export class SessionModalComponent implements OnChanges {
  @Input() isVisible = false;
  @Input() data: SessionModalData | null = null;
  @Output() modalClosed = new EventEmitter<void>();
  @Output() actionPerformed = new EventEmitter<string>();

  lastAccessTime = new Date().toLocaleString();

  isRefreshing = false;
  userProfile: UserProfile | null = null;

  constructor(private authService: AuthService) {
    this.authService.userProfile$.subscribe(profile => {
      this.userProfile = profile;
    });
  }

  closeModal(): void {
    this.isVisible = false;
    this.modalClosed.emit();
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  refreshToken(): void {
    this.isRefreshing = true;
    this.authService.refreshToken().subscribe({
      next: (token) => {
        this.isRefreshing = false;
        this.actionPerformed.emit('token-refreshed');
        this.closeModal();
      },
      error: (error) => {
        this.isRefreshing = false;
        console.error('Error refreshing token:', error);
        this.actionPerformed.emit('refresh-failed');
        // Show error message or redirect to login
        this.logout();
      }
    });
  }

  extendSession(): void {
    this.refreshToken();
  }

  logout(): void {
    // Si la sesión fue cerrada externamente, hacemos un logout local para no re-redirigir a Microsoft
    const isLocalOnly = this.data?.type === 'cross-app-logout';
    this.authService.logout(isLocalOnly);
    this.actionPerformed.emit('logout');
    this.closeModal();
  }

  syncLogin(): void {
    this.actionPerformed.emit('login-sync');
    this.closeModal();
    // Set a flag in sessionStorage to tell app.component to trigger login
    sessionStorage.setItem('explicit_sso_sync', 'true');
    // Use window.location.reload to trigger full initialization and login
    window.location.reload();
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  getInitials(name: string): string {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && changes['isVisible'].currentValue) {
      this.lastAccessTime = new Date().toLocaleString();
    }
  }
}